import { FormEvent, useEffect, useState } from "react";
import AdminShell from "@/components/admin/AdminShell";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import type { AppSettings } from "@/types/database";

type IntegrationStatus = {
  stripe: {
    secret_key: boolean;
    webhook_secret: boolean;
    site_url: boolean;
    webhooks_received: number;
  };
  email: {
    resend_api_key: boolean;
    from_address: boolean;
    sent_logged: number;
  };
  reminders: { cron_secret: boolean };
};

function StatusPill({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-inter ${
        ok
          ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
          : "border-amber-500/30 bg-amber-500/10 text-amber-100"
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${ok ? "bg-emerald-400" : "bg-amber-300"}`} />
      {label}: {ok ? "Configured" : "Not set"}
    </span>
  );
}

const SettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [status, setStatus] = useState<IntegrationStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [form, setForm] = useState({
    notification_email: "",
    default_due_days_before: 7,
    reminder_7d_enabled: true,
    alert_2d_enabled: true,
    email_sign_off: "Olivia Flemming",
    home_postcode: "",
    fuel_included_miles: 10,
    ref_start_number: 50,
    fuel_rate_pounds: "1.00",
  });

  useEffect(() => {
    document.title = "Settings · Olivia Flemming Admin";
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!supabase || !isSupabaseConfigured) {
        setLoading(false);
        return;
      }
      const { data, error: err } = await supabase
        .from("app_settings")
        .select("*")
        .eq("id", 1)
        .maybeSingle();
      if (!alive) return;
      if (err) setError(err.message);
      else if (data) {
        const s = data as AppSettings;
        setForm({
          notification_email: s.notification_email ?? "",
          default_due_days_before: s.default_due_days_before ?? 7,
          reminder_7d_enabled: s.reminder_7d_enabled ?? true,
          alert_2d_enabled: s.alert_2d_enabled ?? true,
          email_sign_off: s.email_sign_off ?? "Olivia Flemming",
          home_postcode: (s as AppSettings & { home_postcode?: string | null }).home_postcode ?? "",
          fuel_included_miles: (s as AppSettings & { fuel_included_miles?: number }).fuel_included_miles ?? 10,
          ref_start_number: (s as AppSettings & { ref_start_number?: number }).ref_start_number ?? 50,
          fuel_rate_pounds: (((s as AppSettings & { fuel_rate_pence?: number }).fuel_rate_pence ?? 100) / 100).toFixed(2),
        });
      }

      const { data: st, error: stErr } = await supabase.functions.invoke("integration-status", {
        method: "POST",
      });
      if (!alive) return;
      if (stErr) {
        setStatusError(
          "Could not reach integration-status (deploy the function to see Stripe/email indicators)."
        );
      } else if (st && !st.error) {
        setStatus(st as IntegrationStatus);
      } else {
        setStatusError((st as { error?: string })?.error || "Status unavailable");
      }

      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, []);

  const onSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!supabase || !isSupabaseConfigured) {
      setError("Supabase is not configured.");
      return;
    }
    setSaving(true);
    setError(null);
    setMessage(null);
    const { error: err } = await supabase.from("app_settings").upsert({
      id: 1,
      notification_email: form.notification_email.trim() || null,
      default_due_days_before: Number(form.default_due_days_before) || 7,
      reminder_7d_enabled: form.reminder_7d_enabled,
      alert_2d_enabled: form.alert_2d_enabled,
      email_sign_off: form.email_sign_off.trim() || "Olivia Flemming",
      home_postcode: form.home_postcode.trim().toUpperCase() || null,
      fuel_included_miles: Math.max(0, Number(form.fuel_included_miles) || 0),
      fuel_rate_pence: Math.max(0, Math.round((Number.parseFloat(form.fuel_rate_pounds) || 0) * 100)),
      ref_start_number: Math.max(1, Math.floor(Number(form.ref_start_number) || 50)),
    });
    setSaving(false);
    if (err) setError(err.message);
    else setMessage("Settings saved.");
  };

  const field =
    "admin-field w-full rounded-xl bg-black/40 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary font-inter text-sm";
  const label = "block text-xs uppercase tracking-widest text-white/50 mb-2";

  return (
    <AdminShell title="Settings">
      {!isSupabaseConfigured && (
        <div className="mb-6 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100/90">
          Connect Supabase env vars to load and save settings.
        </div>
      )}

      {loading ? (
        <p className="text-white/50 text-sm">Loading…</p>
      ) : (
        <form onSubmit={onSave} className="max-w-xl space-y-8">
          <section className="admin-card p-5 md:p-6 space-y-4">
            <h2 className="font-playfair text-xl">Notifications</h2>
            <div>
              <label className={label} htmlFor="notify">
                Olivia notification email
              </label>
              <input
                id="notify"
                type="email"
                className={field}
                value={form.notification_email}
                onChange={(e) => setForm((f) => ({ ...f, notification_email: e.target.value }))}
                placeholder="olivia@example.com"
              />
              <p className="text-xs text-white/40 mt-2 font-inter">
                Used for 2-day unpaid alerts and payment confirmations.
              </p>
            </div>
            <div>
              <label className={label} htmlFor="signoff">
                Email sign-off
              </label>
              <input
                id="signoff"
                className={field}
                value={form.email_sign_off}
                onChange={(e) => setForm((f) => ({ ...f, email_sign_off: e.target.value }))}
              />
            </div>
          </section>

          <section className="admin-card p-5 md:p-6 space-y-4">
            <h2 className="font-playfair text-xl">Fuel charge</h2>
            <div>
              <label className={label} htmlFor="home">Home postcode</label>
              <input
                id="home"
                className={field}
                value={form.home_postcode}
                onChange={(e) => setForm((f) => ({ ...f, home_postcode: e.target.value }))}
                placeholder="e.g. BS1 4DJ"
              />
              <p className="text-xs text-white/40 mt-2 font-inter">Distances are worked out by road from here, there and back. Only you can see this.</p>
            </div>
            <div>
              <label className={label} htmlFor="refStart">Invoice numbers start from each year</label>
              <input
                id="refStart"
                type="number"
                min={1}
                className={field}
                value={form.ref_start_number}
                onChange={(e) => setForm((f) => ({ ...f, ref_start_number: Number(e.target.value) }))}
              />
              <p className="text-xs text-white/40 mt-2 font-inter">Each new year starts again from this number, e.g. OF-{new Date().getFullYear() + 1}-{String(form.ref_start_number || 50).padStart(3, "0")}. Raise it to jump ahead this year too.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={label} htmlFor="incMiles">Free miles</label>
                <input
                  id="incMiles"
                  type="number"
                  min={0}
                  className={field}
                  value={form.fuel_included_miles}
                  onChange={(e) => setForm((f) => ({ ...f, fuel_included_miles: Number(e.target.value) }))}
                />
              </div>
              <div>
                <label className={label} htmlFor="rate">£ per mile after</label>
                <input
                  id="rate"
                  type="number"
                  min={0}
                  step="0.05"
                  className={field}
                  value={form.fuel_rate_pounds}
                  onChange={(e) => setForm((f) => ({ ...f, fuel_rate_pounds: e.target.value }))}
                />
              </div>
            </div>
          </section>

          <section className="admin-card p-5 md:p-6 space-y-4">
            <h2 className="font-playfair text-xl">Reminders (Europe/London)</h2>
            <label className="flex items-start gap-3 text-sm font-inter text-white/80">
              <input
                type="checkbox"
                className="mt-1"
                checked={form.reminder_7d_enabled}
                onChange={(e) =>
                  setForm((f) => ({ ...f, reminder_7d_enabled: e.target.checked }))
                }
              />
              <span>
                <strong className="text-white">7-day client reminder</strong>
                <span className="block text-white/45 text-xs mt-1">
                  If still unpaid 7 days before the performance, email the client a pay link (once).
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3 text-sm font-inter text-white/80">
              <input
                type="checkbox"
                className="mt-1"
                checked={form.alert_2d_enabled}
                onChange={(e) => setForm((f) => ({ ...f, alert_2d_enabled: e.target.checked }))}
              />
              <span>
                <strong className="text-white">2-day Olivia alert</strong>
                <span className="block text-white/45 text-xs mt-1">
                  If still unpaid 2 days before, mark Payment Attention Required and email Olivia.
                </span>
              </span>
            </label>
            <div>
              <label className={label} htmlFor="dueDays">
                Default due days before performance
              </label>
              <input
                id="dueDays"
                type="number"
                min={0}
                className={field}
                value={form.default_due_days_before}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    default_due_days_before: Number(e.target.value),
                  }))
                }
              />
            </div>
          </section>

          <section className="admin-card p-5 md:p-6 space-y-4">
            <h2 className="font-playfair text-xl">Integrations</h2>
            <p className="text-xs text-white/45 font-inter">
              API secrets live only in Supabase Edge Function secrets — they are never shown in this
              UI.
            </p>
            {statusError && (
              <p className="text-xs text-amber-200/90 font-inter">{statusError}</p>
            )}
            {status && (
              <div className="space-y-4">
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-white/40 mb-2">Stripe</p>
                  <div className="flex flex-wrap gap-2">
                    <StatusPill ok={status.stripe.secret_key} label="Secret key" />
                    <StatusPill ok={status.stripe.webhook_secret} label="Webhook secret" />
                    <StatusPill ok={status.stripe.site_url} label="SITE_URL" />
                  </div>
                  <p className="text-xs text-white/40 mt-2 font-inter">
                    Webhook events received: {status.stripe.webhooks_received}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-white/40 mb-2">Email</p>
                  <div className="flex flex-wrap gap-2">
                    <StatusPill ok={status.email.resend_api_key} label="Resend API" />
                    <StatusPill ok={status.email.from_address} label="From address" />
                  </div>
                  <p className="text-xs text-white/40 mt-2 font-inter">
                    Sent emails logged: {status.email.sent_logged}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-white/40 mb-2">
                    Reminders
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <StatusPill ok={status.reminders.cron_secret} label="CRON_SECRET" />
                  </div>
                </div>
              </div>
            )}
            {!status && !statusError && (
              <p className="text-xs text-white/40 font-inter">Checking Edge secrets…</p>
            )}
          </section>

          {error && (
            <p className="text-sm text-red-300" role="alert">
              {error}
            </p>
          )}
          {message && (
            <p className="text-sm text-primary" role="status">
              {message}
            </p>
          )}

          <button
            type="submit"
            disabled={saving || !isSupabaseConfigured}
            className="rounded-full bg-primary text-black px-8 py-3.5 text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save settings"}
          </button>
        </form>
      )}
    </AdminShell>
  );
};

export default SettingsPage;
